import {
  ACCOUNT_WORDS,
  BOOKING_BADGES,
  BOOKING_WORDS,
  CITY_WORDS,
  CLASS_WORDS,
  ENROLMENT_WORDS,
  EVENT_WORDS,
  PAYMENT_WORDS,
  PROVIDER_WORDS,
  QUESTION_WORDS,
  type AccountState,
  type BookingState,
  type CityState,
  type ClassState,
  type EnrolmentState,
  type EventState,
  type PaymentState,
  type ProviderState,
  type QuestionState,
} from '@hi/contracts/shared'
import { Badge, type BadgeProps } from '../ui/Badge.js'

/**
 * A state, written as the word the whole product uses for it.
 *
 * The words come from `@hi/contracts/shared`, which the API reads too. This
 * component exists so no screen ever writes one itself: the sleep bands were
 * *Badly, Alright and Well* on one screen and *Good, Normal and Bad* on the next,
 * and the status words were on their way to being three sets of the same problem.
 *
 * `short` takes the badge word rather than the long one — a table chip says
 * *Cancelled* where a booking's own screen says *Cancelled by the client*.
 */
export type StatusKind =
  | 'booking'
  | 'enrolment'
  | 'payment'
  | 'provider'
  | 'account'
  | 'city'
  | 'question'
  | 'class'
  | 'event'

const TONES: Record<string, BadgeProps['tone']> = {
  // Went well, or costs nothing.
  confirmed: 'success',
  completed: 'success',
  live: 'success',
  succeeded: 'success',
  enrolled: 'success',
  // Held, limited, or on its way.
  pending: 'attention',
  processing: 'attention',
  started: 'attention',
  held: 'attention',
  invited: 'attention',
  paused: 'attention',
  // Refused, or ended badly.
  refused: 'error',
  no_show: 'error',
  expired: 'error',
  abandoned: 'error',
  removed: 'error',
  // Added with the account, city and question sets.
  active: 'success',
  open: 'success',
  answered: 'success',
  suspended: 'error',
  closed: 'neutral',
  inactive: 'neutral',
  pending_verification: 'attention',
  coming_soon: 'attention',
  // A class and an event. `live`, `cancelled` and `closed` are already above and
  // mean the same kind of thing here, which is why this map is keyed by the value
  // rather than by the kind.
  published: 'success',
  finished: 'neutral',
  draft: 'neutral',
  archived: 'neutral',
}

interface Common {
  /** The badge word rather than the long one. Only bookings have two. */
  short?: boolean
  size?: BadgeProps['size']
}

/**
 * A union rather than `{ kind: StatusKind; status: string }`.
 *
 * With a loose `status`, nothing stopped a screen passing an event's state under
 * `kind="class"` — the lookup would miss, the fallback would print the raw value,
 * and `archived` would appear on a screen as the word `archived`. That is the
 * failure this component exists to prevent, reachable through its own front door.
 *
 * Pairing each kind with its own states makes it a compile error. Done now,
 * before any screen calls it: changing this later means touching every call site,
 * and the call sites are exactly the places that would already be wrong.
 */
export type StatusWordProps = Common &
  (
    | { kind: 'booking'; status: BookingState }
    | { kind: 'enrolment'; status: EnrolmentState }
    | { kind: 'payment'; status: PaymentState }
    | { kind: 'provider'; status: ProviderState }
    | { kind: 'account'; status: AccountState }
    | { kind: 'city'; status: CityState }
    | { kind: 'question'; status: QuestionState }
    | { kind: 'class'; status: ClassState }
    | { kind: 'event'; status: EventState }
  )

export function StatusWord({ kind, status, short, size }: StatusWordProps) {
  // A lookup rather than a ladder: seven kinds nested as ternaries is where the
  // eighth gets added to the wrong branch.
  const WORDS: Record<StatusKind, Record<string, string | undefined>> = {
    booking: short ? BOOKING_BADGES : BOOKING_WORDS,
    enrolment: ENROLMENT_WORDS as Record<EnrolmentState, string>,
    payment: PAYMENT_WORDS as Record<PaymentState, string>,
    provider: PROVIDER_WORDS as Record<ProviderState, string>,
    account: ACCOUNT_WORDS as Record<AccountState, string>,
    city: CITY_WORDS as Record<CityState, string>,
    question: QUESTION_WORDS as Record<QuestionState, string>,
    class: CLASS_WORDS as Record<ClassState, string>,
    event: EVENT_WORDS as Record<EventState, string>,
  }
  const word = WORDS[kind][status as string]

  return (
    <Badge tone={TONES[status as string] ?? 'neutral'} size={size}>
      {/* The raw value only if the vocabulary has no word for it. The union above
          makes the wrong kind a compile error and a standing assertion keeps each
          vocabulary complete, so seeing one here means a check has broken. */}
      {word ?? status}
    </Badge>
  )
}
